// Data module for the ציר זמן (timeline) tab of the customer page.
//
// Owns:
// - the SHARED 'contact-details:{contact.id}' cache key fetcher (the raw
//   POST /api/v1/contacts/get-details response — same contract as the overview
//   tab; dates arrive as ISO strings on the wire even though the schema types
//   them as Date, so every reader must normalize — see normalizeTimeline),
// - normalization of timeline actions into render-ready entries,
// - client-side AI enrichment of accessible calls (summary quote + audio URL)
//   joined onto server timeline 'call' entries by time proximity — derived
//   from the calls tab's shared caches ('calls:{id}' + per-call transcript
//   keys), so visiting both tabs never duplicates Firestore reads,
// - the quick-note composer write path (ported from useSellerNotes'
//   saveNotes/addNote logic — NOT importing the legacy hook/component).
import { firebaseApp, db } from "firebaseConfig";
import { WEBOT_SERVER_ENDPOINT } from "logic/Consts";
import { getAudioUrlFromCallSummary } from "utils/calls/display";
import { fetchCached, getCached, setCached, subscribeCached } from "../../data/sessionCache";
import { CONTACT_DETAILS_MAX_AGE_MS } from "../overview/overviewData";
import {
  contactCallsKey,
  fetchCallsForContact,
  callTranscriptKey,
  fetchCallTranscript,
  CALLS_MAX_AGE_MS,
  TRANSCRIPT_MAX_AGE_MS
} from "../calls/callsData";
import {
  USER_NOTES_COLLECTION_NAME,
  userNotesDocId
} from "../../../../../../@types/sharedSchema";
import type {
  CallSummary,
  REST_schema,
  RealEstateContact,
  UserNote,
  UserNotesDocument
} from "../../../../../../@types/sharedSchema";
import type { DisplayUserNote } from "hooks/useListingNotes";

// Single freshness budget for the SHARED contact-details key — both writers
// (overview + timeline, preload + hooks) must pass the same value, otherwise
// the shorter age re-fires the full get-details dossier on every re-hover.
export { CONTACT_DETAILS_MAX_AGE_MS };

// ── Shared contact-details cache (identical contract to the overview tab) ────

export const contactDetailsCacheKey = (contactId: string): string =>
  `contact-details:${contactId}`;

/**
 * Fetches the FULL contact dossier (no dataSources subset — the shared cache
 * key contract is "the POST /api/v1/contacts/get-details response", and the
 * timeline needs every source anyway; the server only includes the note +
 * signature timeline riders on full requests).
 */
export async function fetchContactDetailsRaw(
  contactId: string
): Promise<REST_schema.ContactDetailsResponseSchema> {
  const currentUser = firebaseApp.auth().currentUser;
  const token = currentUser ? await currentUser.getIdToken() : undefined;
  const response = await fetch(`${WEBOT_SERVER_ENDPOINT}/api/v1/contacts/get-details`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {})
    },
    body: JSON.stringify({ phoneNumberInternational: contactId })
  });
  if (!response.ok) {
    throw new Error(`contacts/get-details failed [status=${response.status}]`);
  }
  return (await response.json()) as REST_schema.ContactDetailsResponseSchema;
}

/** Fire-and-forget warm-up of the shared contact-details key. Never throws. */
export function preloadTimelineData(contact: RealEstateContact | null | undefined): void {
  if (!contact?.id) return;
  const contactId = contact.id;
  void fetchCached(
    contactDetailsCacheKey(contactId),
    () => fetchContactDetailsRaw(contactId),
    { maxAgeMs: CONTACT_DETAILS_MAX_AGE_MS }
  ).catch(() => undefined);
}

// ── Normalized timeline entries ──────────────────────────────────────────────

/** Org-blur placeholder used by the backend for other users' free text. */
export const BLUR_PLACEHOLDER = "****";

export type TimelineEntry = {
  key: string;
  /** Tolerant string — ContactTimelineActionType today, future values render as generic. */
  actionType: string;
  description: string;
  time: Date | null;
  timeMs: number;
  user: { userId: string; userName: string; userAvatar: string };
  /** Literal '****' description — org-blurred free text; render a "hidden" hint, never the asterisks. */
  isBlurred: boolean;
};

const toDateOrNull = (value: unknown): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value as string);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Flattens the get-details response into render-ready entries, newest first.
 * Tolerant reader: handles Date objects AND ISO strings for actionTime, and
 * unknown/future actionType values.
 */
export function normalizeTimeline(
  response: REST_schema.ContactDetailsResponseSchema | undefined
): TimelineEntry[] {
  if (!Array.isArray(response)) return [];
  const entries: TimelineEntry[] = [];
  response.forEach((detail, detailIndex) => {
    (detail?.timeline ?? []).forEach((action, index) => {
      if (!action?.actionType) return;
      const time = toDateOrNull(action.actionTime);
      entries.push({
        key: `${detailIndex}:${index}:${action.actionType}:${time?.getTime() ?? "na"}`,
        actionType: action.actionType,
        description: action.actionDescription ?? "",
        time,
        timeMs: time?.getTime() ?? 0,
        user: {
          userId: action.actionUser?.userId ?? "",
          userName: action.actionUser?.userName || action.actionUser?.userId || "",
          userAvatar: action.actionUser?.userAvatar ?? ""
        },
        isBlurred: action.actionDescription === BLUR_PLACEHOLDER
      });
    });
  });
  return entries.sort((a, b) => b.timeMs - a.timeMs);
}

// ── AI call enrichment (derived from the calls tab's shared caches) ─────────

export type TimelineCallEnrichment = {
  callId: string;
  timeMs: number;
  audioUrl: string | null;
  aiSummary: string | null;
};

export const timelineCallEnrichmentCacheKey = (contactId: string): string =>
  `timeline:${contactId}:call-enrichment`;

/** Cap enrichment reads — one transcript doc read per call. */
const CALL_ENRICHMENT_LIMIT = 25;

/**
 * Loads the accessible calls with this contact and their AI summaries +
 * recording URLs. Fully derived from the calls tab's session caches:
 * - call list via the SHARED 'calls:{id}' key (own VNs + org-admin teammates'
 *   VNs, ordered + limited server-side — same visibility as the שיחות tab, so
 *   an org admin sees identical AI badges on both tabs),
 * - transcripts via the calls tab's per-call keys ('calls:{id}:transcript:*'),
 * so visiting both tabs costs one set of Firestore reads, not two.
 * Recording-URL fallback logic is getAudioUrlFromCallSummary (ported util,
 * not the player component). Best-effort: any failing branch yields [].
 */
export async function loadTimelineCallEnrichments(
  contactId: string
): Promise<TimelineCallEnrichment[]> {
  if (!contactId) return [];

  let calls: CallSummary[];
  try {
    calls = await fetchCached(contactCallsKey(contactId), () => fetchCallsForContact(contactId), {
      maxAgeMs: CALLS_MAX_AGE_MS
    });
  } catch {
    return [];
  }

  // The join key with server timeline entries is createdTime (the server emits
  // actionTime = call.createdTime) — drop docs without it, like the server does.
  const recentCalls = (calls ?? [])
    .filter((call) => call.createdTime instanceof Date)
    .slice(0, CALL_ENRICHMENT_LIMIT);

  return Promise.all(
    recentCalls.map(async (call) => {
      let aiSummary: string | null = null;
      try {
        const transcription = await fetchCached(
          callTranscriptKey(contactId, call),
          () => fetchCallTranscript(call),
          { maxAgeMs: TRANSCRIPT_MAX_AGE_MS }
        );
        const summaryText = transcription?.AI?.callSummary ?? transcription?.summary ?? "";
        aiSummary = summaryText.trim() ? summaryText.trim() : null;
      } catch {
        // enrichment is best-effort — the entry still renders from the server text
      }
      return {
        callId: call.id ?? "",
        timeMs: (call.createdTime as Date).getTime(),
        audioUrl: getAudioUrlFromCallSummary(call) || null,
        aiSummary
      };
    })
  );
}

/**
 * Matches a server timeline 'call' entry to one of the user's own enriched
 * calls. Both sides derive from the same calls-summary doc createdTime, so an
 * (almost) exact time match is expected; tolerance covers serialization jitter.
 */
const CALL_JOIN_TOLERANCE_MS = 3000;

export function findCallEnrichment(
  entry: TimelineEntry,
  enrichments: TimelineCallEnrichment[] | undefined
): TimelineCallEnrichment | null {
  if (!enrichments?.length || entry.actionType !== "call" || !entry.timeMs) return null;
  let best: TimelineCallEnrichment | null = null;
  let bestDelta = CALL_JOIN_TOLERANCE_MS + 1;
  for (const enrichment of enrichments) {
    const delta = Math.abs(enrichment.timeMs - entry.timeMs);
    if (delta <= CALL_JOIN_TOLERANCE_MS && delta < bestDelta) {
      best = enrichment;
      bestDelta = delta;
    }
  }
  return best;
}

// ── Quick-note composer write path (ported from useSellerNotes logic) ────────

// Keep the doc-level organizationIds the union of per-note orgs, and ABSENT
// (not []) when nothing is shared — the org array-contains query and the
// Firestore rules both rely on this invariant (see useSellerNotes).
function computeDocOrgIds(notes: UserNote[]): string[] | undefined {
  const set = new Set<string>();
  for (const note of notes) {
    for (const orgId of note.organizationIds ?? []) set.add(orgId);
  }
  return set.size > 0 ? Array.from(set) : undefined;
}

/**
 * Appends a quick note to the user's own user-notes doc for this contact
 * (creating the doc when missing). One-shot read-then-set — existing note
 * objects are written back untouched (their noteTime values stay raw Firestore
 * Timestamps, which Firestore accepts on write).
 */
export async function addTimelineQuickNote(params: {
  contactId: string;
  userEmail: string;
  text: string;
}): Promise<UserNote> {
  const { contactId, userEmail } = params;
  const text = params.text.trim();
  if (!text) throw new Error("empty note");

  const newNote: UserNote = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    note: text,
    noteTime: new Date()
  };

  const snapshot = await db
    .collection(USER_NOTES_COLLECTION_NAME)
    .where("userId", "==", userEmail)
    .where("contactId", "==", contactId)
    .limit(1)
    .get();

  if (!snapshot.empty) {
    const docRef = snapshot.docs[0].ref;
    const data = snapshot.docs[0].data() as UserNotesDocument;
    const updatedNotes = [newNote, ...(data.notes ?? [])];
    const docOrgIds = computeDocOrgIds(updatedNotes);
    const docData: UserNotesDocument = {
      userId: userEmail,
      contactId,
      notes: updatedNotes,
      ...(docOrgIds ? { organizationIds: docOrgIds } : {})
    };
    await docRef.set(docData);
  } else {
    // Create with the deterministic id the backend + notes/overview composers
    // also use (the query above already covers legacy auto-id docs), so
    // concurrent first-note creation from two surfaces converges on ONE doc —
    // .add() here could permanently split a contact's notes across two docs.
    const docData: UserNotesDocument = { userId: userEmail, contactId, notes: [newNote] };
    await db
      .collection(USER_NOTES_COLLECTION_NAME)
      .doc(userNotesDocId({ userId: userEmail, contactId }))
      .set(docData);
  }

  return newNote;
}

// How long we keep watching the details key for a stale revalidation that
// clobbers the optimistic note action, and how close a server note action's
// time must be to count as "our" note (server actionTime === noteTime, so the
// tolerance only covers serialization jitter).
const QUICK_NOTE_GUARD_WINDOW_MS = 30_000;
const QUICK_NOTE_TIME_TOLERANCE_MS = 2_000;

const timelineContainsNote = (
  details: REST_schema.ContactDetailsResponseSchema | undefined,
  note: UserNote
): boolean => {
  if (!Array.isArray(details)) return false;
  const noteTime = toDateOrNull(note.noteTime);
  if (!noteTime) return false;
  const noteTimeMs = noteTime.getTime();
  return details.some((detail) =>
    (detail?.timeline ?? []).some((action) => {
      if (action?.actionType !== "note") return false;
      if ((action.actionDescription ?? "") !== note.note) return false;
      const actionTime = toDateOrNull(action.actionTime);
      return !!actionTime && Math.abs(actionTime.getTime() - noteTimeMs) <= QUICK_NOTE_TIME_TOLERANCE_MS;
    })
  );
};

/**
 * Optimistically reflects a just-saved quick note in the session caches:
 * - 'notes:{id}' (DisplayUserNote[] newest-first, shared with the notes/
 *   overview tabs) — prepended only when the cache is already warm (seeding a
 *   cold cache with a single note would masquerade as the full list) AND the
 *   note isn't already there: when the own-notes live listener is mounted
 *   (overview is the default tab), Firestore latency compensation mirrors the
 *   new note into this key BEFORE the write promise resolves, so an
 *   unconditional prepend would double-count it (badge + duplicate React keys).
 * - 'contact-details:{id}' — a synthetic 'note' timeline action so the
 *   timeline shows the note instantly without a server round-trip. Because
 *   sessionCache's background revalidation overwrites the entry
 *   unconditionally, a get-details fetch already in flight when the note was
 *   saved can clobber the synthetic action with a response that predates the
 *   write — so we briefly watch the key and re-prepend until a copy of the
 *   note (server rider or synthetic) is present.
 */
export function applyQuickNoteToCaches(
  contactId: string,
  note: UserNote,
  author: { email: string; name?: string; avatar?: string }
): void {
  const notesKey = `notes:${contactId}`;
  const cachedNotes = getCached<DisplayUserNote[]>(notesKey);
  if (Array.isArray(cachedNotes) && !cachedNotes.some((cached) => cached?.id === note.id)) {
    // Stamp the DisplayUserNote fields the shared-key contract expects.
    const displayNote: DisplayUserNote = { ...note, isMine: true, authorEmail: author.email };
    setCached(notesKey, [displayNote, ...cachedNotes]);
  }

  const detailsKey = contactDetailsCacheKey(contactId);
  const prependSyntheticAction = (): void => {
    const cachedDetails = getCached<REST_schema.ContactDetailsResponseSchema>(detailsKey);
    if (!Array.isArray(cachedDetails) || !cachedDetails[0]) return;
    const optimisticAction: REST_schema.ContactTimelineAction = {
      actionType: "note",
      actionDescription: note.note,
      actionTime: note.noteTime,
      actionUser: {
        userId: author.email,
        userMail: author.email,
        userName: author.name || author.email,
        userAvatar: author.avatar ?? ""
      }
    };
    const [first, ...rest] = cachedDetails;
    setCached(detailsKey, [
      { ...first, timeline: [optimisticAction, ...(first.timeline ?? [])] },
      ...rest
    ]);
  };

  prependSyntheticAction();

  // Guard against a stale in-flight revalidation wiping the optimistic action:
  // on every cache write within the window, re-prepend if the note vanished.
  // Re-entrancy is safe — the re-prepend's own notify finds the note present.
  const unsubscribe = subscribeCached(detailsKey, () => {
    const current = getCached<REST_schema.ContactDetailsResponseSchema>(detailsKey);
    if (Array.isArray(current) && current[0] && !timelineContainsNote(current, note)) {
      prependSyntheticAction();
    }
  });
  setTimeout(unsubscribe, QUICK_NOTE_GUARD_WINDOW_MS);
}
